import React from 'react';

import { Box, type BoxProps } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';

type IconComponent = React.FC;

// Size mapping to match style-closet theme.space values exactly
export const sizeMap: Record<number, string> = {
  1: token('spacing.space.50'),
  2: '6px',
  3: token('spacing.space.100'),
  4: token('spacing.space.150'),
  5: '14px',
  6: token('spacing.space.200'),
  7: '20px',
  8: token('spacing.space.300'),
  9: token('spacing.space.400'),
  10: token('spacing.space.600'),
  11: token('spacing.space.800'),
  12: '96px',
};

const AdviceIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" ry="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.3637 26.7274C12.64 26.7274 8 22.0874 8 16.3637C8 10.64 12.64 6 18.3637 6C24.0604 6 28.6835 10.5963 28.727 16.2827H28.7274C28.7274 16.2966 28.7273 16.3105 28.7273 16.3243C28.7273 16.3374 28.7273 16.3506 28.7273 16.3637C28.7273 17.1069 28.6491 17.8318 28.5004 18.5307C27.281 24.5292 21.4467 28.6753 18.3637 30L18.3637 26.7274Z"
      fill={token('colors.primitives.navy.100')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.5828 20.7254C24.5247 19.5916 25.091 18.1347 25.091 16.5455C25.091 12.9305 22.1605 10 18.5455 10C14.9305 10 12 12.9305 12 16.5455C12 18.1347 12.5663 19.5916 13.5082 20.7254L18.5455 18.8364L23.5828 20.7254ZM18.5619 23.091H18.5291C18.5346 23.091 18.54 23.091 18.5455 23.091C18.551 23.091 18.5564 23.091 18.5619 23.091Z"
      fill={token('colors.primitives.gold.50')}
    />
  </svg>
);

const AdviceIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" ry="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.3637 26.7274C12.64 26.7274 8 22.0874 8 16.3637C8 10.64 12.64 6 18.3637 6C24.0604 6 28.6835 10.5963 28.727 16.2827H28.7274C28.7274 16.2966 28.7273 16.3105 28.7273 16.3243C28.7273 16.3374 28.7273 16.3506 28.7273 16.3637C28.7273 17.1069 28.6491 17.8318 28.5004 18.5307C27.281 24.5292 21.4467 28.6753 18.3637 30L18.3637 26.7274Z"
      fill={token('colors.primitives.blue.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.5828 20.7254C24.5247 19.5916 25.091 18.1347 25.091 16.5455C25.091 12.9305 22.1605 10 18.5455 10C14.9305 10 12 12.9305 12 16.5455C12 18.1347 12.5663 19.5916 13.5082 20.7254L18.5455 18.8364L23.5828 20.7254ZM18.5619 23.091H18.5291C18.5346 23.091 18.54 23.091 18.5455 23.091C18.551 23.091 18.5564 23.091 18.5619 23.091Z"
      fill={token('colors.primitives.gold.50')}
    />
  </svg>
);

const AppleIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M16.7303 12.7064L17.2206 13.0129C17.6975 13.3109 18.3025 13.3109 18.7794 13.0129L19.2697 12.7064C20.9318 11.6677 23.0482 11.6988 24.6789 12.7859C26.6203 14.0802 27.4048 16.5476 26.5672 18.7254L24.2107 24.8522C23.5113 26.6707 21.6034 27.7207 19.6929 27.3386L19.5689 27.3138C18.5332 27.1066 17.4668 27.1066 16.4311 27.3138L16.3071 27.3386C14.3966 27.7207 12.4887 26.6707 11.7893 24.8522L9.43284 18.7254C8.59524 16.5476 9.37967 14.0802 11.3211 12.7859C12.9518 11.6988 15.0682 11.6677 16.7303 12.7064Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path d="M18 14V10V8" stroke={token('colors.primitives.teal.80')} strokeWidth="2" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24 7C24 9.76142 21.7614 12 19 12C19 9.23858 21.2386 7 24 7Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const AppleIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M16.7303 12.7064L17.2206 13.0129C17.6975 13.3109 18.3025 13.3109 18.7794 13.0129L19.2697 12.7064C20.9318 11.6677 23.0482 11.6988 24.6789 12.7859C26.6203 14.0802 27.4048 16.5476 26.5672 18.7254L24.2107 24.8522C23.5113 26.6707 21.6034 27.7207 19.6929 27.3386L19.5689 27.3138C18.5332 27.1066 17.4668 27.1066 16.4311 27.3138L16.3071 27.3386C14.3966 27.7207 12.4887 26.6707 11.7893 24.8522L9.43284 18.7254C8.59524 16.5476 9.37967 14.0802 11.3211 12.7859C12.9518 11.6988 15.0682 11.6677 16.7303 12.7064Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path d="M18 14V10V8" stroke={token('colors.primitives.teal.80')} strokeWidth="2" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24 7C24 9.76142 21.7614 12 19 12C19 9.23858 21.2386 7 24 7Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const BabyIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.3286 25.7361C13.881 27.2454 15.7714 28 18 28C20.2286 28 22.119 27.2454 23.6714 25.7361C25.2238 24.2269 26 22.3889 26 20.2222C26 18.7963 25.7571 17.3611 25.2714 15.9167C25.1112 15.4401 24.9354 14.9781 24.744 14.5308L21.3202 17.3839L21.0001 17.6506L20.6801 17.3839L17.9999 15.1508L15.3202 17.3839L15.0001 17.6506L14.68 17.3839L11.256 14.5307C11.0647 14.978 10.8888 15.44 10.7286 15.9167C10.2429 17.3611 10 18.7963 10 20.2222C10 22.3889 10.7762 24.2269 12.3286 25.7361ZM11.6913 13.5917L15.0001 16.3489L17.6798 14.1159L17.9999 13.8492L18.3199 14.1159L21.0001 16.3489L24.3088 13.5918C24.0279 13.0313 23.7203 12.4961 23.3857 11.9861C22.6143 10.8102 21.7571 9.85185 20.8143 9.11111C19.8714 8.37037 18.9333 8 18 8C17.0857 8 16.1524 8.37037 15.2 9.11111C14.2476 9.85185 13.3857 10.8102 12.6143 11.9861C12.2798 12.496 11.9721 13.0312 11.6913 13.5917Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path d="M24.5 14L21 17L18 14.5L15 17L11.5 14" stroke="#071B39" />
  </svg>
);

const BabyIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.3286 25.7361C13.881 27.2454 15.7714 28 18 28C20.2286 28 22.119 27.2454 23.6714 25.7361C25.2238 24.2269 26 22.3889 26 20.2222C26 18.7963 25.7571 17.3611 25.2714 15.9167C25.1112 15.4401 24.9354 14.9781 24.744 14.5308L21.3202 17.3839L21.0001 17.6506L20.6801 17.3839L17.9999 15.1508L15.3202 17.3839L15.0001 17.6506L14.68 17.3839L11.256 14.5307C11.0647 14.978 10.8888 15.44 10.7286 15.9167C10.2429 17.3611 10 18.7963 10 20.2222C10 22.3889 10.7762 24.2269 12.3286 25.7361ZM11.6913 13.5917L15.0001 16.3489L17.6798 14.1159L17.9999 13.8492L18.3199 14.1159L21.0001 16.3489L24.3088 13.5918C24.0279 13.0313 23.7203 12.4961 23.3857 11.9861C22.6143 10.8102 21.7571 9.85185 20.8143 9.11111C19.8714 8.37037 18.9333 8 18 8C17.0857 8 16.1524 8.37037 15.2 9.11111C14.2476 9.85185 13.3857 10.8102 12.6143 11.9861C12.2798 12.496 11.9721 13.0312 11.6913 13.5917Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path d="M24.5 14L21 17L18 14.5L15 17L11.5 14" stroke="#071B39" />
  </svg>
);

const BandaidIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M22 14L14 14L14 10C14 8.89543 14.8954 8 16 8L20 8C21.1046 8 22 8.89543 22 10L22 14Z"
      fill={token('colors.primitives.teal.80')}
    />
    <path
      d="M22 22L14 22L14 26C14 27.1046 14.8954 28 16 28L20 28C21.1046 28 22 27.1046 22 26L22 22Z"
      fill={token('colors.primitives.teal.80')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28 16C28 14.8954 27.1046 14 26 14L10 14C8.89543 14 8 14.8954 8 16L8 20C8 21.1046 8.89543 22 10 22L26 22C27.1046 22 28 21.1046 28 20L28 16ZM19 20C19 20.5523 19.4477 21 20 21C20.5523 21 21 20.5523 21 20C21 19.4477 20.5523 19 20 19C19.4477 19 19 19.4477 19 20ZM20 17C19.4477 17 19 16.5523 19 16C19 15.4477 19.4477 15 20 15C20.5523 15 21 15.4477 21 16C21 16.5523 20.5523 17 20 17ZM16 17C15.4477 17 15 16.5523 15 16C15 15.4477 15.4477 15 16 15C16.5523 15 17 15.4477 17 16C17 16.5523 16.5523 17 16 17ZM16 21C15.4477 21 15 20.5523 15 20C15 19.4477 15.4477 19 16 19C16.5523 19 17 19.4477 17 20C17 20.5523 16.5523 21 16 21Z"
      fill={token('colors.primitives.teal.60')}
    />
    <circle cx="16" cy="16" r="1" fill={token('colors.primitives.navy.100')} />
    <circle cx="16" cy="20" r="1" fill={token('colors.primitives.navy.100')} />
    <circle cx="20" cy="16" r="1" fill={token('colors.primitives.navy.100')} />
    <circle cx="20" cy="20" r="1" fill={token('colors.primitives.navy.100')} />
  </svg>
);

const BandaidIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M22 14L14 14L14 10C14 8.89543 14.8954 8 16 8L20 8C21.1046 8 22 8.89543 22 10L22 14Z"
      fill={token('colors.primitives.teal.80')}
    />
    <path
      d="M22 22L14 22L14 26C14 27.1046 14.8954 28 16 28L20 28C21.1046 28 22 27.1046 22 26L22 22Z"
      fill={token('colors.primitives.teal.80')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M28 16C28 14.8954 27.1046 14 26 14L10 14C8.89543 14 8 14.8954 8 16L8 20C8 21.1046 8.89543 22 10 22L26 22C27.1046 22 28 21.1046 28 20L28 16ZM19 20C19 20.5523 19.4477 21 20 21C20.5523 21 21 20.5523 21 20C21 19.4477 20.5523 19 20 19C19.4477 19 19 19.4477 19 20ZM20 17C19.4477 17 19 16.5523 19 16C19 15.4477 19.4477 15 20 15C20.5523 15 21 15.4477 21 16C21 16.5523 20.5523 17 20 17ZM16 17C15.4477 17 15 16.5523 15 16C15 15.4477 15.4477 15 16 15C16.5523 15 17 15.4477 17 16C17 16.5523 16.5523 17 16 17ZM16 21C15.4477 21 15 20.5523 15 20C15 19.4477 15.4477 19 16 19C16.5523 19 17 19.4477 17 20C17 20.5523 16.5523 21 16 21Z"
      fill={token('colors.primitives.teal.60')}
    />
    <circle cx="16" cy="16" r="1" fill={token('colors.primitives.navy.100')} />
    <circle cx="16" cy="20" r="1" fill={token('colors.primitives.navy.100')} />
    <circle cx="20" cy="16" r="1" fill={token('colors.primitives.navy.100')} />
    <circle cx="20" cy="20" r="1" fill={token('colors.primitives.navy.100')} />
  </svg>
);

const BondIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_1214_7794)">
      <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
      <g clipPath="url(#clip1_1214_7794)">
        <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
        <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
        <rect x="7" y="11" width="22" height="13" fill={token('colors.primitives.blue.20')} />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7 9H29V25H7V9ZM25 11H11C11 11 11 11 11 11C11 12.1046 10.1045 13 9 13V21C10.1045 21.0001 10.9999 21.8955 11 23H25C25 21.8954 25.8954 21 27 21C27 21 26.9999 21 27 21L27 13C27 13 27 13 27 13C25.8954 13 25 12.1046 25 11C25 11 25 11 25 11Z"
          fill={token('colors.primitives.blue.60')}
        />
        <circle cx="14" cy="17" r="3" fill={token('colors.primitives.blue.60')} />
        <path d="M25 15H19" stroke={token('colors.primitives.blue.60')} strokeWidth="2" />
        <path d="M25 19H19" stroke={token('colors.primitives.blue.60')} strokeWidth="2" />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_1214_7794">
        <rect width="36" height="36" rx="4" fill="white" />
      </clipPath>
      <clipPath id="clip1_1214_7794">
        <rect width="36" height="36" rx="4" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const BondIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_1214_7805)">
      <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
      <g clipPath="url(#clip0_1214_7805)">
        <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
        <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
        <rect x="7" y="11" width="22" height="13" fill={token('colors.primitives.blue.20')} />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7 9H29V25H7V9ZM25 11H11C11 11 11 11 11 11C11 12.1046 10.1045 13 9 13V21C10.1045 21.0001 10.9999 21.8955 11 23H25C25 21.8954 25.8954 21 27 21C27 21 26.9999 21 27 21L27 13C27 13 27 13 27 13C25.8954 13 25 12.1046 25 11C25 11 25 11 25 11Z"
          fill={token('colors.primitives.blue.60')}
        />
        <circle cx="14" cy="17" r="3" fill={token('colors.primitives.blue.60')} />
        <path d="M25 15H19" stroke={token('colors.primitives.blue.60')} strokeWidth="2" />
        <path d="M25 19H19" stroke={token('colors.primitives.blue.60')} strokeWidth="2" />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_1214_7805">
        <rect width="36" height="36" rx="4" fill="white" />
      </clipPath>
      <clipPath id="clip1_1214_7805">
        <rect width="36" height="36" rx="4" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const BusinessIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 27V9H26V27H24V19H20V27H10ZM12 19H18V25H12V19Z"
      fill={token('colors.primitives.teal.60')}
    />
    <rect x="20" y="19" width="4" height="8" fill="#071B39" />
    <rect x="12" y="19" width="6" height="6" fill="#071B39" />
    <path
      d="M26 11H10L8.21115 14.5777C8.07229 14.8554 8 15.1616 8 15.4721V17H28V15.4721C28 15.1616 27.9277 14.8554 27.7889 14.5777L26 11Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const BusinessIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 27V9H26V27H24V19H20V27H10ZM12 19H18V25H12V19Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path
      d="M26 11H10L8.21115 14.5777C8.07229 14.8554 8 15.1616 8 15.4721V17H28V15.4721C28 15.1616 27.9277 14.8554 27.7889 14.5777L26 11Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const CapIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path d="M27 23V16.1L18 21L7 15L18 9L29 15V23H27Z" fill="#1C1B1F" />
    <path d="M11 23.2002L18 27.0002L25 23.2002V18.2002L18 22.0002L11 18.2002V23.2002Z" fill="#071B39" />
    <path d="M11 23.2001L18 27.0001L25 23.2001V15.1998L18 18.9998L11 15.1998V23.2001Z" fill="#071B39" />
    <rect x="27" y="15" width="2" height="8" fill={token('colors.primitives.gold.50')} />
    <path d="M18 21L7 15L18 9L29 15L18 21Z" fill="#1856BA" />
  </svg>
);

const CapIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path d="M27 23V16.1L18 21L7 15L18 9L29 15V23H27Z" fill="#1C1B1F" />
    <path d="M11 23.2002L18 27.0002L25 23.2002V18.2002L18 22.0002L11 18.2002V23.2002Z" fill="#1856BA" />
    <path d="M11 23.2001L18 27.0001L25 23.2001V15.1998L18 18.9998L11 15.1998V23.2001Z" fill="#1856BA" />
    <rect x="27" y="15" width="2" height="8" fill={token('colors.primitives.gold.50')} />
    <path d="M18 21L7 15L18 9L29 15L18 21Z" fill="#2D79ED" />
  </svg>
);

const CarIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.3188 12H22.6812L23.9669 15H12.0331L13.3188 12ZM27 22V17L24.5195 11.2122C24.2043 10.4768 23.4813 10 22.6812 10H13.3188C12.5187 10 11.7957 10.4768 11.4805 11.2122L9 17V22V24H11H25H27V22Z"
      fill={token('colors.primitives.blue.60')}
    />
    <circle cx="12.5" cy="18.5" r="1.5" fill="white" />
    <circle cx="23.5" cy="18.5" r="1.5" fill="white" />
    <path d="M9 24H12V27H9V24Z" fill="#071B39" />
    <path d="M24 24H27V27H24V24Z" fill="#071B39" />
  </svg>
);

const CarIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.3188 12H22.6812L23.9669 15H12.0331L13.3188 12ZM27 22V17L24.5195 11.2122C24.2043 10.4768 23.4813 10 22.6812 10H13.3188C12.5187 10 11.7957 10.4768 11.4805 11.2122L9 17V22V24H11H25H27V22Z"
      fill={token('colors.primitives.blue.60')}
    />
    <circle cx="12.5" cy="18.5" r="1.5" fill={token('colors.primitives.blue.20')} />
    <circle cx="23.5" cy="18.5" r="1.5" fill={token('colors.primitives.blue.20')} />
    <path d="M9 24H12V27H9V24Z" fill="#12438F" />
    <path d="M24 24H27V27H24V24Z" fill="#12438F" />
  </svg>
);

const CardIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <rect x="11" y="8" width="14" height="20" rx="2" fill={token('colors.primitives.blue.60')} />
    <rect x="18" y="12" width="3" height="4" fill={token('colors.primitives.blue.20')} />
  </svg>
);

const CardIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <rect x="11" y="8" width="14" height="20" rx="2" fill={token('colors.primitives.blue.60')} />
    <rect x="18" y="12" width="3" height="4" fill={token('colors.primitives.blue.20')} />
  </svg>
);

const CharityIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.5892 14.7509C15.4242 14.3461 16 13.4903 16 12.5C16 11.1193 14.8807 10 13.5 10C12.1193 10 11 11.1193 11 12.5C11 13.2125 11.298 13.8553 11.7762 14.3107C10.2769 14.0053 8.79459 13.5885 7.34174 13.0602L6.65826 14.9398C8.0649 15.4513 9.4975 15.8646 10.9461 16.1797L8 26H11L13 19L14 26H17L15.1633 16.8165C16.1071 16.8974 17.0536 16.9379 18 16.9379V14.9379C16.8613 14.9379 15.7227 14.8756 14.5892 14.7509Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24.2238 14.3107C24.702 13.8553 25 13.2125 25 12.5C25 11.1193 23.8807 10 22.5 10C21.1193 10 20 11.1193 20 12.5C20 13.4903 20.5758 14.3461 21.4108 14.7509C20.2773 14.8756 19.1387 14.9379 18 14.9379V16.9379C18.9464 16.9379 19.8929 16.8974 20.8367 16.8165L19 26H22L23 19L25 26H28L25.0539 16.1797C26.5025 15.8646 27.9351 15.4513 29.3417 14.9398L28.6582 13.0602C27.2054 13.5885 25.7231 14.0053 24.2238 14.3107Z"
      fill={token('colors.primitives.gold.50')}
    />
  </svg>
);

const CharityIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.5892 14.7509C15.4242 14.3461 16 13.4903 16 12.5C16 11.1193 14.8807 10 13.5 10C12.1193 10 11 11.1193 11 12.5C11 13.2125 11.298 13.8553 11.7762 14.3107C10.2769 14.0053 8.79459 13.5885 7.34174 13.0602L6.65826 14.9398C8.0649 15.4513 9.4975 15.8646 10.9461 16.1797L8 26H11L13 19L14 26H17L15.1633 16.8165C16.1071 16.8974 17.0536 16.9379 18 16.9379V14.9379C16.8613 14.9379 15.7227 14.8756 14.5892 14.7509Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24.2238 14.3107C24.702 13.8553 25 13.2125 25 12.5C25 11.1193 23.8807 10 22.5 10C21.1193 10 20 11.1193 20 12.5C20 13.4903 20.5758 14.3461 21.4108 14.7509C20.2773 14.8756 19.1387 14.9379 18 14.9379V16.9379C18.9464 16.9379 19.8929 16.8974 20.8367 16.8165L19 26H22L23 19L25 26H28L25.0539 16.1797C26.5025 15.8646 27.9351 15.4513 29.3417 14.9398L28.6582 13.0602C27.2054 13.5885 25.7231 14.0053 24.2238 14.3107Z"
      fill={token('colors.primitives.gold.50')}
    />
  </svg>
);

const CoinIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <circle cx="18" cy="18" r="9" fill="#FCFCD0" stroke={token('colors.primitives.gold.50')} strokeWidth="2" />
    <circle cx="18" cy="18" r="5" fill={token('colors.primitives.gold.50')} />
  </svg>
);

const CoinIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <circle cx="18" cy="18" r="9" fill={token('colors.primitives.gold.50')} stroke="#FCFCD0" strokeWidth="2" />
    <circle cx="18" cy="18" r="5" fill="#FCFCD0" />
  </svg>
);

const DollarIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <rect x="7" y="11" width="22" height="14" fill={token('colors.primitives.teal.60')} />
    <circle cx="18" cy="18" r="3" fill={token('colors.primitives.teal.80')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M25 13H11C11 13 11 13 11 13.0001C11 14.1046 10.1045 15 9 15.0001V21.0001C10.1045 21.0001 10.9999 21.8955 11 23H25C25 21.8955 25.8954 21.0001 27 21.0001C27 21.0001 27 21.0001 27 21.0001V15.0001C27 15.0001 27 15.0001 27 15.0001C25.8954 15.0001 25 14.1046 25 13.0001C25 13 25 13 25 13ZM7 11H9H27H29V13V23V25H27H9H7V23V13V11Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const DollarIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <rect x="7" y="11" width="22" height="14" fill={token('colors.primitives.teal.60')} />
    <circle cx="18" cy="18" r="3" fill={token('colors.primitives.teal.80')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M25 13H11C11 13 11 13 11 13.0001C11 14.1046 10.1045 15 9 15.0001V21.0001C10.1045 21.0001 10.9999 21.8955 11 23H25C25 21.8955 25.8954 21.0001 27 21.0001C27 21.0001 27 21.0001 27 21.0001V15.0001C27 15.0001 27 15.0001 27 15.0001C25.8954 15.0001 25 14.1046 25 13.0001C25 13 25 13 25 13ZM7 11H9H27H29V13V23V25H27H9H7V23V13V11Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const BriefcaseIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M13.666 9.33329V11.5H15.8327V9.33329H20.166V11.5H22.3327V9.33329C22.3327 8.73746 22.1205 8.22739 21.6962 7.80308C21.2719 7.37878 20.7618 7.16663 20.166 7.16663H15.8327C15.2368 7.16663 14.7268 7.37878 14.3025 7.80308C13.8782 8.22739 13.666 8.73746 13.666 9.33329Z"
      fill={token('colors.primitives.navy.100')}
    />
    <path
      d="M7.16602 13.6667V20.1667H28.8327V13.6667C28.8327 13.0708 28.6205 12.5608 28.1962 12.1365C27.7719 11.7122 27.2619 11.5 26.666 11.5H9.33268C8.73685 11.5 8.22678 11.7122 7.80247 12.1365C7.37817 12.5608 7.16602 13.0708 7.16602 13.6667Z"
      fill="#2C78EA"
    />
    <path
      d="M7.80247 27.1135C8.22678 27.5378 8.73685 27.75 9.33268 27.75H26.666C27.2619 27.75 27.7719 27.5378 28.1962 27.1135C28.6205 26.6892 28.8327 26.1791 28.8327 25.5833V20.1666H7.16602V25.5833C7.16602 26.1791 7.37817 26.6892 7.80247 27.1135Z"
      fill={token('colors.primitives.navy.100')}
    />
    <rect x="15.834" y="18" width="4.33333" height="4.33333" fill="#F5B500" />
  </svg>
);

const BriefcaseIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M13.666 9.33329V11.5H15.8327V9.33329H20.166V11.5H22.3327V9.33329C22.3327 8.73746 22.1205 8.22739 21.6962 7.80308C21.2719 7.37878 20.7618 7.16663 20.166 7.16663H15.8327C15.2368 7.16663 14.7268 7.37878 14.3025 7.80308C13.8782 8.22739 13.666 8.73746 13.666 9.33329Z"
      fill="#0042AB"
    />
    <path
      d="M7.16602 13.6667V20.1667H28.8327V13.6667C28.8327 13.0708 28.6205 12.5608 28.1962 12.1365C27.7719 11.7122 27.2619 11.5 26.666 11.5H9.33268C8.73685 11.5 8.22678 11.7122 7.80247 12.1365C7.37817 12.5608 7.16602 13.0708 7.16602 13.6667Z"
      fill="#2C78EA"
    />
    <path
      d="M7.80247 27.1135C8.22678 27.5378 8.73685 27.75 9.33268 27.75H26.666C27.2619 27.75 27.7719 27.5378 28.1962 27.1135C28.6205 26.6892 28.8327 26.1791 28.8327 25.5833V20.1666H7.16602V25.5833C7.16602 26.1791 7.37817 26.6892 7.80247 27.1135Z"
      fill="#0042AB"
    />
    <rect x="15.834" y="18" width="4.33333" height="4.33333" fill="#F5B500" />
  </svg>
);

const HomeIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path d="M10 27V17H8V15L18 8L28 15V17H26V27H20V19H16V27H10Z" fill={token('colors.primitives.gold.50')} />
    <path d="M8 17H10L18 11L26 17H28V15L18 8L8 15V17Z" fill={token('colors.primitives.orange.60')} />
    <rect x="16" y="19" width="4" height="8" fill="#071B39" />
  </svg>
);

const HomeIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path d="M10 27V17H8V15L18 8L28 15V17H26V27H20V19H16V27H10Z" fill={token('colors.primitives.gold.50')} />
    <path d="M8 17H10L18 11L26 17H28V15L18 8L8 15V17Z" fill={token('colors.primitives.orange.60')} />
  </svg>
);

const LiferingIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 22C20.2091 22 22 20.2091 22 18C22 15.7909 20.2091 14 18 14C15.7909 14 14 15.7909 14 18C14 20.2091 15.7909 22 18 22ZM18 28C23.5228 28 28 23.5228 28 18C28 12.4772 23.5228 8 18 8C12.4772 8 8 12.4772 8 18C8 23.5228 12.4772 28 18 28Z"
      fill={token('colors.primitives.blue.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.1355 19.0362C14.5036 20.4127 15.5876 21.4965 16.964 21.8646L12.4853 26.3433C11.3633 25.6002 10.3999 24.6369 9.65683 23.5149L14.1355 19.0362ZM16.9638 14.1355C15.5874 14.5037 14.5035 15.5876 14.1354 16.964L9.65674 12.4853C10.3998 11.3633 11.3632 10.4 12.4852 9.65687L16.9638 14.1355ZM26.3432 23.5149C25.6 24.6368 24.6367 25.6002 23.5147 26.3433L19.036 21.8646C20.4124 21.4965 21.4964 20.4126 21.8645 19.0362L26.3432 23.5149ZM23.5148 9.6569C24.6368 10.4 25.6002 11.3634 26.3432 12.4854L21.8645 16.9641C21.4965 15.5876 20.4126 14.5037 19.0362 14.1356L23.5148 9.6569Z"
      fill="#071B39"
    />
  </svg>
);

const LiferingIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 22C20.2091 22 22 20.2091 22 18C22 15.7909 20.2091 14 18 14C15.7909 14 14 15.7909 14 18C14 20.2091 15.7909 22 18 22ZM18 28C23.5228 28 28 23.5228 28 18C28 12.4772 23.5228 8 18 8C12.4772 8 8 12.4772 8 18C8 23.5228 12.4772 28 18 28Z"
      fill={token('colors.primitives.blue.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.1355 19.0362C14.5036 20.4127 15.5876 21.4965 16.964 21.8646L12.4853 26.3433C11.3633 25.6002 10.3999 24.6369 9.65683 23.5149L14.1355 19.0362ZM16.9638 14.1355C15.5874 14.5037 14.5035 15.5876 14.1354 16.964L9.65674 12.4853C10.3998 11.3633 11.3632 10.4 12.4852 9.65687L16.9638 14.1355ZM26.3432 23.5149C25.6 24.6368 24.6367 25.6002 23.5147 26.3433L19.036 21.8646C20.4124 21.4965 21.4964 20.4126 21.8645 19.0362L26.3432 23.5149ZM23.5148 9.6569C24.6368 10.4 25.6002 11.3634 26.3432 12.4854L21.8645 16.9641C21.4965 15.5876 20.4126 14.5037 19.0362 14.1356L23.5148 9.6569Z"
      fill={token('colors.primitives.blue.20')}
    />
  </svg>
);

const ParasolIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path d="M10 26L25 11" stroke="#071B39" strokeWidth="2" />
    <path
      d="M25.0711 10.9289C21.1658 7.02369 14.8342 7.02369 10.9289 10.9289L25.0711 25.0711C28.9763 21.1658 28.9763 14.8342 25.0711 10.9289Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path
      d="M25.0711 10.9289C23.1184 8.97631 18.3697 10.5592 14.4645 14.4645L21.5355 21.5355C25.4408 17.6303 27.0237 12.8816 25.0711 10.9289Z"
      fill={token('colors.primitives.gold.50')}
    />
  </svg>
);

const ParasolIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path d="M10 26L25 11" stroke="#FCFCD0" strokeWidth="2" />
    <path
      d="M25.0711 10.9289C21.1658 7.02369 14.8342 7.02369 10.9289 10.9289L25.0711 25.0711C28.9763 21.1658 28.9763 14.8342 25.0711 10.9289Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path
      d="M25.0711 10.9289C23.1184 8.97631 18.3697 10.5592 14.4645 14.4645L21.5355 21.5355C25.4408 17.6303 27.0237 12.8816 25.0711 10.9289Z"
      fill={token('colors.primitives.gold.50')}
    />
  </svg>
);

const PlaneIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M14.5 28V26.5L16.5 25V19.5L8 22V20L16.5 15V9.5C16.5 9.08333 16.6458 8.72917 16.9375 8.4375C17.2292 8.14583 17.5833 8 18 8C18.4167 8 18.7708 8.14583 19.0625 8.4375C19.3542 8.72917 19.5 9.08333 19.5 9.5V15L28 20V22L19.5 19.5V25L21.5 26.5V28L18 27L14.5 28Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path d="M28 20L19.5 15V19.5L28 22V20Z" fill={token('colors.primitives.teal.80')} />
    <path d="M8 22L16.5 19.5V15L8 20V22Z" fill={token('colors.primitives.teal.80')} />
  </svg>
);

const PlaneIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M14.5 28V26.5L16.5 25V19.5L8 22V20L16.5 15V9.5C16.5 9.08333 16.6458 8.72917 16.9375 8.4375C17.2292 8.14583 17.5833 8 18 8C18.4167 8 18.7708 8.14583 19.0625 8.4375C19.3542 8.72917 19.5 9.08333 19.5 9.5V15L28 20V22L19.5 19.5V25L21.5 26.5V28L18 27L14.5 28Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path d="M28 20L19.5 15V19.5L28 22V20Z" fill={token('colors.primitives.teal.80')} />
    <path d="M8 22L16.5 19.5V15L8 20V22Z" fill={token('colors.primitives.teal.80')} />
  </svg>
);

const PlantIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 14C12 17.3137 14.6863 20 18 20C18 16.6863 15.3137 14 12 14Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19 15.748C22.4505 14.8599 25 11.7277 25 8C20.5817 8 17 11.5817 17 16V22H19L19 15.748Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path d="M26 28H10L15.1716 22.8284C16.7337 21.2663 19.2663 21.2663 20.8284 22.8284L26 28Z" fill="#071B39" />
  </svg>
);

const PlantIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 14C12 17.3137 14.6863 20 18 20C18 16.6863 15.3137 14 12 14Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19 15.748C22.4505 14.8599 25 11.7277 25 8C20.5817 8 17 11.5817 17 16V22H19L19 15.748Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path
      d="M26 28H10L15.1716 22.8284C16.7337 21.2663 19.2663 21.2663 20.8284 22.8284L26 28Z"
      fill={token('colors.primitives.teal.80')}
    />
  </svg>
);

const PresentIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M8 14C8 12.8954 8.89543 12 10 12H26C27.1046 12 28 12.8954 28 14V25C28 26.1046 27.1046 27 26 27H10C8.89543 27 8 26.1046 8 25L8 14Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15 10C14.4477 10 14 10.4477 14 11C14 11.5523 14.4477 12 15 12C15.5523 12 16 11.5523 16 11C16 10.4477 15.5523 10 15 10ZM12 11C12 9.34315 13.3431 8 15 8C15.08 8 15.1594 8.00314 15.2378 8.00929C16.0878 8.07582 16.8754 8.4967 17.4033 9.17541L18 9.94261L18.5967 9.17541C19.1246 8.49673 19.9121 8.07586 20.762 8.0093C20.8405 8.00314 20.9199 8 21 8C22.6569 8 24 9.34315 24 11C24 12.6056 22.7386 13.9166 21.1528 13.9962L23.7894 17.3861L22.2106 18.6139L19 14.486V27H17V14.486L13.7894 18.6139L12.2106 17.3861L14.8472 13.9962C13.2614 13.9166 12 12.6056 12 11ZM21 10C20.4477 10 20 10.4477 20 11C20 11.5523 20.4477 12 21 12C21.5523 12 22 11.5523 22 11C22 10.4477 21.5523 10 21 10Z"
      fill="#071B39"
    />
  </svg>
);

const PresentIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M8 14C8 12.8954 8.89543 12 10 12H26C27.1046 12 28 12.8954 28 14V25C28 26.1046 27.1046 27 26 27H10C8.89543 27 8 26.1046 8 25L8 14Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15 10C14.4477 10 14 10.4477 14 11C14 11.5523 14.4477 12 15 12C15.5523 12 16 11.5523 16 11C16 10.4477 15.5523 10 15 10ZM12 11C12 9.34315 13.3431 8 15 8C15.08 8 15.1594 8.00314 15.2378 8.00929C16.0878 8.07582 16.8754 8.4967 17.4033 9.17541L18 9.94261L18.5967 9.17541C19.1246 8.49673 19.9121 8.07586 20.762 8.0093C20.8405 8.00314 20.9199 8 21 8C22.6569 8 24 9.34315 24 11C24 12.6056 22.7386 13.9166 21.1528 13.9962L23.7894 17.3861L22.2106 18.6139L19 14.486V27H17V14.486L13.7894 18.6139L12.2106 17.3861L14.8472 13.9962C13.2614 13.9166 12 12.6056 12 11ZM21 10C20.4477 10 20 10.4477 20 11C20 11.5523 20.4477 12 21 12C21.5523 12 22 11.5523 22 11C22 10.4477 21.5523 10 21 10Z"
      fill="#FCFCD0"
    />
  </svg>
);

const SignPostIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" ry="4" fill={token('colors.primitives.grey.10')} />
    <path d="M20.4747 4.08789H15.6621V33.5909H20.4747V4.08789Z" fill={token('colors.primitives.gold.50')} />
    <path
      d="M28.2447 16.1601L31.3994 13.052L28.2447 9.94629H7.8756V16.1601H28.2447Z"
      fill={token('colors.primitives.navy.100')}
    />
    <path
      d="M10.6442 18.8994L8.35254 21.1196L10.6442 23.3379L25.4406 23.3379L25.4406 18.8994L10.6442 18.8994Z"
      fill={token('colors.primitives.teal.60')}
    />
  </svg>
);

const SignPostIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" ry="4" fill={token('colors.primitives.grey.90')} />
    <path d="M20.4747 4.08789H15.6621V33.5909H20.4747V4.08789Z" fill={token('colors.primitives.gold.50')} />
    <path
      d="M28.2447 16.1601L31.3994 13.052L28.2447 9.94629H7.8756V16.1601H28.2447Z"
      fill={token('colors.primitives.blue.60')}
    />
    <path
      d="M10.6442 18.8994L8.35254 21.1196L10.6442 23.3379L25.4406 23.3379L25.4406 18.8994L10.6442 18.8994Z"
      fill={token('colors.primitives.teal.60')}
    />
  </svg>
);

const TalkingIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" ry="4" fill={token('colors.primitives.grey.10')} />
    <mask
      id="mask0_4758_67096"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x="3"
      y="3"
      width="24"
      height="32"
    >
      <rect x="3.75" y="3.38184" width="23.25" height="30.8889" fill={token('colors.primitives.grey.10')} />
    </mask>
    <g mask="url(#mask0_4758_67096)">
      <path
        d="M8.78742 10.2968L8.72531 10.4432C7.92507 12.3295 8.37838 14.5143 9.863 15.9265C10.8592 16.874 11.4231 18.1887 11.4231 19.5635V23.4372C11.4231 23.8492 11.6155 24.2376 11.9433 24.4871L18.0055 29.1016L17.3227 25.0052C17.254 24.593 17.2195 24.1758 17.2195 23.7579V20.9066C17.2195 20.5423 17.5149 20.2469 17.8792 20.2469H19.7961C20.4991 20.2469 21.0787 19.6957 21.1139 18.9935L21.2196 16.8866C21.2259 16.7608 21.3033 16.6496 21.419 16.5999L21.9274 16.3818C22.3604 16.196 22.4647 15.6293 22.1262 15.3016L21.1985 14.4031C20.9812 14.1928 20.8721 13.8947 20.9022 13.5938L21.0129 12.4864C21.0381 12.2344 21.0167 11.98 20.9496 11.7358C20.1972 8.99469 17.7054 7.09473 14.8629 7.09473H13.6242C11.5154 7.09473 9.61099 8.35552 8.78742 10.2968Z"
        fill={token('colors.primitives.orange.50')}
      />
      <path
        d="M18.3148 25.042L18.0155 24.7434C15.664 22.3972 11.838 22.4608 9.56562 24.8837C8.55906 25.9569 7.84862 27.2733 7.50391 28.7037L7.00637 30.7683C6.42927 33.1631 6.12203 35.615 6.09037 38.0781L5.67693 70.2521L33.4509 65.6289L28.5696 46.582L26.9734 41.4185C26.5566 40.0701 26.0225 38.7608 25.3773 37.5055L20.5326 28.0796C19.956 26.9578 19.2077 25.9329 18.3148 25.042Z"
        fill={token('colors.primitives.navy.100')}
      />
      <path
        d="M18.141 12.852L18.272 12.8345C19.9966 12.6046 21.2598 11.0952 21.1823 9.35708C21.1069 7.66427 19.7818 6.29304 18.0925 6.15968L16.4681 6.03143C14.5661 5.88128 12.6538 6.13084 10.8541 6.76407L10.7684 6.79422C9.1897 7.3497 7.93693 8.57414 7.34547 10.1398L7.11093 10.7606C6.77679 11.6451 6.54117 12.5637 6.40835 13.4999L4.896 24.1591H14.3695V16.8071L13.7091 16.2483C13.3148 15.9147 13.0723 15.4357 13.0368 14.9205L12.9833 14.1447C12.936 13.4593 13.4792 12.8773 14.1662 12.8773C14.8211 12.8773 15.352 13.4082 15.352 14.0631V14.2247C15.352 14.3487 15.4525 14.4493 15.5765 14.4493H16.1539C16.2656 14.4493 16.3688 14.3896 16.4244 14.2927L16.6952 13.8217C17.0002 13.2913 17.5345 12.9329 18.141 12.852Z"
        fill={token('colors.primitives.red.90')}
      />
      <circle
        cx="0.928485"
        cy="0.928485"
        r="0.928485"
        transform="matrix(-1 0 0 1 14.8364 15.8525)"
        fill={token('colors.primitives.gold.50')}
      />
    </g>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M33.8069 10C34.0619 10 34.2686 10.2067 34.2686 10.4617V16.1749C34.2686 16.4298 34.0619 16.6365 33.8069 16.6365H27.8693L26.3978 18.1781C26.254 18.3287 26 18.227 26 18.0187V16.2014H26.0009C26.0004 16.1926 26.0001 16.1837 26.0001 16.1749V10.4617C26.0001 10.2067 26.2068 10 26.4618 10H33.8069Z"
      fill={token('colors.primitives.teal.60')}
    />
  </svg>
);

const TalkingIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" ry="4" fill={token('colors.primitives.grey.90')} />
    <mask
      id="mask0_4758_67096"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x="3"
      y="3"
      width="20"
      height="32"
    >
      <rect x="3.75" y="3.38184" width="23.25" height="30.8889" fill={token('colors.primitives.grey.90')} />
    </mask>
    <g mask="url(#mask0_4758_67096)">
      <path
        d="M8.78742 10.2968L8.72531 10.4432C7.92507 12.3295 8.37838 14.5143 9.863 15.9265C10.8592 16.874 11.4231 18.1887 11.4231 19.5635V23.4372C11.4231 23.8492 11.6155 24.2376 11.9433 24.4871L18.0055 29.1016L17.3227 25.0052C17.254 24.593 17.2195 24.1758 17.2195 23.7579V20.9066C17.2195 20.5423 17.5149 20.2469 17.8792 20.2469H19.7961C20.4991 20.2469 21.0787 19.6957 21.1139 18.9935L21.2196 16.8866C21.2259 16.7608 21.3033 16.6496 21.419 16.5999L21.9274 16.3818C22.3604 16.196 22.4647 15.6293 22.1262 15.3016L21.1985 14.4031C20.9812 14.1928 20.8721 13.8947 20.9022 13.5938L21.0129 12.4864C21.0381 12.2344 21.0167 11.98 20.9496 11.7358C20.1972 8.99469 17.7054 7.09473 14.8629 7.09473H13.6242C11.5154 7.09473 9.61099 8.35552 8.78742 10.2968Z"
        fill={token('colors.primitives.orange.50')}
      />
      <path
        d="M18.3148 25.042L18.0155 24.7434C15.664 22.3972 11.838 22.4608 9.56562 24.8837C8.55906 25.9569 7.84862 27.2733 7.50391 28.7037L7.00637 30.7683C6.42927 33.1631 6.12203 35.615 6.09037 38.0781L5.67693 70.2521L33.4509 65.6289L28.5696 46.582L26.9734 41.4185C26.5566 40.0701 26.0225 38.7608 25.3773 37.5055L20.5326 28.0796C19.956 26.9578 19.2077 25.9329 18.3148 25.042Z"
        fill={token('colors.primitives.blue.60')}
      />
      <path
        d="M18.141 12.852L18.272 12.8345C19.9966 12.6046 21.2598 11.0952 21.1823 9.35708C21.1069 7.66427 19.7818 6.29304 18.0925 6.15968L16.4681 6.03143C14.5661 5.88128 12.6538 6.13084 10.8541 6.76407L10.7684 6.79422C9.1897 7.3497 7.93693 8.57414 7.34547 10.1398L7.11093 10.7606C6.77679 11.6451 6.54117 12.5637 6.40835 13.4999L4.896 24.1591H14.3695V16.8071L13.7091 16.2483C13.3148 15.9147 13.0723 15.4357 13.0368 14.9205L12.9833 14.1447C12.936 13.4593 13.4792 12.8773 14.1662 12.8773C14.8211 12.8773 15.352 13.4082 15.352 14.0631V14.2247C15.352 14.3487 15.4525 14.4493 15.5765 14.4493H16.1539C16.2656 14.4493 16.3688 14.3896 16.4244 14.2927L16.6952 13.8217C17.0002 13.2913 17.5345 12.9329 18.141 12.852Z"
        fill={token('colors.primitives.red.90')}
      />
      <circle
        cx="0.928485"
        cy="0.928485"
        r="0.928485"
        transform="matrix(-1 0 0 1 14.8364 15.8525)"
        fill={token('colors.primitives.gold.50')}
      />
    </g>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M33.8069 10C34.0619 10 34.2686 10.2067 34.2686 10.4617V16.1749C34.2686 16.4298 34.0619 16.6365 33.8069 16.6365H27.8693L26.3978 18.1781C26.254 18.3287 26 18.227 26 18.0187V16.2014H26.0009C26.0004 16.1926 26.0001 16.1837 26.0001 16.1749V10.4617C26.0001 10.2067 26.2068 10 26.4618 10H33.8069Z"
      fill={token('colors.primitives.teal.60')}
    />
  </svg>
);

const TreeIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path d="M20 28H16L17 18H19L20 28Z" fill="#071B39" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.1332 9.13568C19.1141 7.62144 16.8859 7.62144 15.8668 9.13568C15.2897 9.99335 14.2549 10.4219 13.2404 10.2236C11.4491 9.87343 9.87343 11.4491 10.2236 13.2404C10.4219 14.2549 9.99335 15.2897 9.13569 15.8668C8.37856 16.3763 8 17.1882 8 18H28C28 17.1882 27.6214 16.3763 26.8643 15.8668C26.0066 15.2897 25.5781 14.2549 25.7764 13.2404C26.1266 11.4491 24.5509 9.87343 22.7596 10.2236C21.7451 10.4219 20.7103 9.99335 20.1332 9.13568Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path d="M18 21L22 18H23L18 24V21Z" fill="#071B39" />
    <path d="M18 21L14 18H13L18 24V21Z" fill="#071B39" />
  </svg>
);

const TreeIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path d="M20 28H16L17 18H19L20 28Z" fill="#DAFCF5" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.1332 9.13568C19.1141 7.62144 16.8859 7.62144 15.8668 9.13568C15.2897 9.99335 14.2549 10.4219 13.2404 10.2236C11.4491 9.87343 9.87343 11.4491 10.2236 13.2404C10.4219 14.2549 9.99335 15.2897 9.13569 15.8668C8.37856 16.3763 8 17.1882 8 18H28C28 17.1882 27.6214 16.3763 26.8643 15.8668C26.0066 15.2897 25.5781 14.2549 25.7764 13.2404C26.1266 11.4491 24.5509 9.87343 22.7596 10.2236C21.7451 10.4219 20.7103 9.99335 20.1332 9.13568Z"
      fill={token('colors.primitives.teal.60')}
    />
    <path d="M18 21L22 18H23L18 24V21Z" fill="#DAFCF5" />
    <path d="M18 21L14 18H13L18 24V21Z" fill="#DAFCF5" />
  </svg>
);

const WateringCanIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M18 28C18 26.7667 18.2375 25.6042 18.7125 24.5125C19.1875 23.4208 19.8333 22.4667 20.65 21.65C21.4667 20.8333 22.4208 20.1875 23.5125 19.7125C24.6042 19.2375 25.7667 19 27 19C27 20.2333 26.7625 21.3958 26.2875 22.4875C25.8125 23.5792 25.1667 24.5333 24.35 25.35C23.5333 26.1667 22.5792 26.8125 21.4875 27.2875C20.3958 27.7625 19.2333 28 18 28C16.7667 28 15.6042 27.7625 14.5125 27.2875C13.4208 26.8125 12.4667 26.1667 11.65 25.35C10.8333 24.5333 10.1875 23.5792 9.7125 22.4875C9.2375 21.3958 9 20.2333 9 19C10.2333 19 11.3958 19.2375 12.4875 19.7125C13.5792 20.1875 14.5333 20.8333 15.35 21.65C16.1667 22.4667 16.8125 23.4208 17.2875 24.5125C17.7625 25.6042 18 26.7667 18 28Z"
      fill={token('colors.primitives.teal.80')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 13C18.2113 13 18.4143 12.9817 18.6091 12.945C18.6748 13.132 18.7604 13.317 18.866 13.4999C18.9717 13.6829 19.0891 13.8496 19.2183 14C19.0891 14.1504 18.9717 14.3171 18.866 14.5001C18.7604 14.683 18.6748 14.868 18.6091 15.055C18.4143 15.0183 18.2113 15 18 15C17.7887 15 17.5857 15.0183 17.3909 15.055C17.3252 14.8679 17.2396 14.6829 17.134 14.4999C17.0283 14.317 16.9109 14.1504 16.7818 14C16.9109 13.8496 17.0283 13.683 17.1339 13.5001C17.2396 13.3171 17.3252 13.1321 17.3909 12.945C17.5857 12.9817 17.7887 13 18 13ZM12.2822 14C11.9717 13.6596 11.753 13.2529 11.6262 12.7797C11.4139 11.9874 11.5178 11.2275 11.9378 10.5001C12.3578 9.77261 12.9639 9.30273 13.7562 9.09043C14.2294 8.96364 14.691 8.94962 15.1411 9.04836C15.2806 8.6092 15.5236 8.21641 15.87 7.87C16.45 7.29 17.16 7 18 7C18.84 7 19.55 7.29 20.13 7.87C20.4764 8.21638 20.7193 8.60912 20.8588 9.04823C21.3089 8.94948 21.7705 8.9635 22.2437 9.0903C23.036 9.30259 23.6422 9.77247 24.0622 10.4999C24.4822 11.2274 24.586 11.9873 24.3737 12.7796C24.2469 13.2528 24.0282 13.6596 23.7177 14C24.0282 14.3404 24.2469 14.7472 24.3737 15.2204C24.586 16.0127 24.4822 16.7726 24.0622 17.5001C23.6422 18.2275 23.036 18.6974 22.2437 18.9097C21.7705 19.0365 21.3089 19.0505 20.8588 18.9518C20.7193 19.3909 20.4764 19.7836 20.13 20.13C19.55 20.71 18.84 21 18 21C17.16 21 16.45 20.71 15.87 20.13C15.5236 19.7836 15.2806 19.3908 15.1411 18.9516C14.691 19.0504 14.2294 19.0364 13.7562 18.9096C12.9639 18.6973 12.3578 18.2274 11.9378 17.4999C11.5178 16.7725 11.4139 16.0126 11.6262 15.2203C11.753 14.7471 11.9717 14.3404 12.2822 14Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path
      d="M20.13 16.13C19.55 16.71 18.84 17 18 17C17.16 17 16.45 16.71 15.87 16.13C15.29 15.55 15 14.84 15 14C15 13.16 15.29 12.45 15.87 11.87C16.45 11.29 17.16 11 18 11C18.84 11 19.55 11.29 20.13 11.87C20.71 12.45 21 13.16 21 14C21 14.84 20.71 15.55 20.13 16.13Z"
      fill="#071B39"
    />
  </svg>
);

const WateringCanIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M18 28C18 26.7667 18.2375 25.6042 18.7125 24.5125C19.1875 23.4208 19.8333 22.4667 20.65 21.65C21.4667 20.8333 22.4208 20.1875 23.5125 19.7125C24.6042 19.2375 25.7667 19 27 19C27 20.2333 26.7625 21.3958 26.2875 22.4875C25.8125 23.5792 25.1667 24.5333 24.35 25.35C23.5333 26.1667 22.5792 26.8125 21.4875 27.2875C20.3958 27.7625 19.2333 28 18 28C16.7667 28 15.6042 27.7625 14.5125 27.2875C13.4208 26.8125 12.4667 26.1667 11.65 25.35C10.8333 24.5333 10.1875 23.5792 9.7125 22.4875C9.2375 21.3958 9 20.2333 9 19C10.2333 19 11.3958 19.2375 12.4875 19.7125C13.5792 20.1875 14.5333 20.8333 15.35 21.65C16.1667 22.4667 16.8125 23.4208 17.2875 24.5125C17.7625 25.6042 18 26.7667 18 28Z"
      fill={token('colors.primitives.teal.80')}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 13C18.2113 13 18.4143 12.9817 18.6091 12.945C18.6748 13.132 18.7604 13.317 18.866 13.4999C18.9717 13.6829 19.0891 13.8496 19.2183 14C19.0891 14.1504 18.9717 14.3171 18.866 14.5001C18.7604 14.683 18.6748 14.868 18.6091 15.055C18.4143 15.0183 18.2113 15 18 15C17.7887 15 17.5857 15.0183 17.3909 15.055C17.3252 14.8679 17.2396 14.6829 17.134 14.4999C17.0283 14.317 16.9109 14.1504 16.7818 14C16.9109 13.8496 17.0283 13.683 17.1339 13.5001C17.2396 13.3171 17.3252 13.1321 17.3909 12.945C17.5857 12.9817 17.7887 13 18 13ZM12.2822 14C11.9717 13.6596 11.753 13.2529 11.6262 12.7797C11.4139 11.9874 11.5178 11.2275 11.9378 10.5001C12.3578 9.77261 12.9639 9.30273 13.7562 9.09043C14.2294 8.96364 14.691 8.94962 15.1411 9.04836C15.2806 8.6092 15.5236 8.21641 15.87 7.87C16.45 7.29 17.16 7 18 7C18.84 7 19.55 7.29 20.13 7.87C20.4764 8.21638 20.7193 8.60912 20.8588 9.04823C21.3089 8.94948 21.7705 8.9635 22.2437 9.0903C23.036 9.30259 23.6422 9.77247 24.0622 10.4999C24.4822 11.2274 24.586 11.9873 24.3737 12.7796C24.2469 13.2528 24.0282 13.6596 23.7177 14C24.0282 14.3404 24.2469 14.7472 24.3737 15.2204C24.586 16.0127 24.4822 16.7726 24.0622 17.5001C23.6422 18.2275 23.036 18.6974 22.2437 18.9097C21.7705 19.0365 21.3089 19.0505 20.8588 18.9518C20.7193 19.3909 20.4764 19.7836 20.13 20.13C19.55 20.71 18.84 21 18 21C17.16 21 16.45 20.71 15.87 20.13C15.5236 19.7836 15.2806 19.3908 15.1411 18.9516C14.691 19.0504 14.2294 19.0364 13.7562 18.9096C12.9639 18.6973 12.3578 18.2274 11.9378 17.4999C11.5178 16.7725 11.4139 16.0126 11.6262 15.2203C11.753 14.7471 11.9717 14.3404 12.2822 14Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path
      d="M20.13 16.13C19.55 16.71 18.84 17 18 17C17.16 17 16.45 16.71 15.87 16.13C15.29 15.55 15 14.84 15 14C15 13.16 15.29 12.45 15.87 11.87C16.45 11.29 17.16 11 18 11C18.84 11 19.55 11.29 20.13 11.87C20.71 12.45 21 13.16 21 14C21 14.84 20.71 15.55 20.13 16.13Z"
      fill="#071B39"
    />
  </svg>
);

const WeddingIconLight: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.10')} />
    <path
      d="M20.6109 9.3312L17.997 12L15.3831 9.3312C15.1377 9.07915 15 8.73829 15 8.38301C15 8.02774 15.1377 7.68689 15.3831 7.43485L15.4262 7.39092C15.6732 7.14053 16.0073 7 16.3555 7C16.7037 7 17.0378 7.14053 17.2848 7.39092L18.0024 8.1231L18.72 7.39092C18.967 7.14053 19.3011 7 19.6493 7C19.9975 7 20.3316 7.14053 20.5786 7.39092L20.6199 7.43485C20.865 7.68763 21.0017 8.02934 21 8.38491C20.9983 8.74048 20.8584 9.08084 20.6109 9.3312Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path
      d="M12 14C12 12.8954 12.8954 12 14 12H22C23.1046 12 24 12.8954 24 14V17H12V14Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path
      d="M10 19C10 17.8954 10.8954 17 12 17H24C25.1046 17 26 17.8954 26 19V22H10V19Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path d="M9 23H27" stroke="#071B39" strokeWidth="2" />
    <path
      d="M9 24V22H27V24H20C19.4477 24 19 24.4477 19 25C19 25.5523 19.4477 26 20 26H21V28H15V26H16C16.5523 26 17 25.5523 17 25C17 24.4477 16.5523 24 16 24H9Z"
      fill="#071B39"
    />
  </svg>
);

const WeddingIconDark: IconComponent = () => (
  <svg viewBox="0 0 36 36" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="36" rx="4" fill={token('colors.primitives.grey.90')} />
    <path
      d="M20.6109 9.3312L17.997 12L15.3831 9.3312C15.1377 9.07915 15 8.73829 15 8.38301C15 8.02774 15.1377 7.68689 15.3831 7.43485L15.4262 7.39092C15.6732 7.14053 16.0073 7 16.3555 7C16.7037 7 17.0378 7.14053 17.2848 7.39092L18.0024 8.1231L18.72 7.39092C18.967 7.14053 19.3011 7 19.6493 7C19.9975 7 20.3316 7.14053 20.5786 7.39092L20.6199 7.43485C20.865 7.68763 21.0017 8.02934 21 8.38491C20.9983 8.74048 20.8584 9.08084 20.6109 9.3312Z"
      fill={token('colors.primitives.orange.60')}
    />
    <path
      d="M12 14C12 12.8954 12.8954 12 14 12H22C23.1046 12 24 12.8954 24 14V17H12V14Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path
      d="M10 19C10 17.8954 10.8954 17 12 17H24C25.1046 17 26 17.8954 26 19V22H10V19Z"
      fill={token('colors.primitives.gold.50')}
    />
    <path d="M9 23H27" stroke="#FCFCD0" strokeWidth="2" />
    <path
      d="M9 24V22H27V24H20C19.4477 24 19 24.4477 19 25C19 25.5523 19.4477 26 20 26H21V28H15V26H16C16.5523 26 17 25.5523 17 25C17 24.4477 16.5523 24 16 24H9Z"
      fill="#FCFCD0"
    />
  </svg>
);

const GoalIconMap = {
  advice: { light: AdviceIconLight, dark: AdviceIconDark },
  apple: { light: AppleIconLight, dark: AppleIconDark },
  baby: { light: BabyIconLight, dark: BabyIconDark },
  bandaid: { light: BandaidIconLight, dark: BandaidIconDark },
  bond: { light: BondIconLight, dark: BondIconDark },
  business: { light: BusinessIconLight, dark: BusinessIconDark },
  cap: { light: CapIconLight, dark: CapIconDark },
  car: { light: CarIconLight, dark: CarIconDark },
  card: { light: CardIconLight, dark: CardIconDark },
  charity: { light: CharityIconLight, dark: CharityIconDark },
  coin: { light: CoinIconLight, dark: CoinIconDark },
  dollar: { light: DollarIconLight, dark: DollarIconDark },
  briefcase: { light: BriefcaseIconLight, dark: BriefcaseIconDark },
  home: { light: HomeIconLight, dark: HomeIconDark },
  lifering: { light: LiferingIconLight, dark: LiferingIconDark },
  parasol: { light: ParasolIconLight, dark: ParasolIconDark },
  plane: { light: PlaneIconLight, dark: PlaneIconDark },
  plant: { light: PlantIconLight, dark: PlantIconDark },
  present: { light: PresentIconLight, dark: PresentIconDark },
  sign_post: { light: SignPostIconLight, dark: SignPostIconDark },
  talking: { light: TalkingIconLight, dark: TalkingIconDark },
  tree: { light: TreeIconLight, dark: TreeIconDark },
  watering_can: { light: WateringCanIconLight, dark: WateringCanIconDark },
  wedding: { light: WeddingIconLight, dark: WeddingIconDark },
};

type GoalIconBaseProps = {
  name: keyof typeof GoalIconMap;
  size?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12; // based on style-closet-react theme.space, kept for compatibility with existing implementations
  mode?: 'light' | 'dark';
};

export type GoalIconProps = Omit<BoxProps, 'as' | 'asChild' | 'children'> & GoalIconBaseProps;

export const GoalIcon: React.FC<GoalIconProps> = ({
  className,
  css: cssProp,
  size,
  name,
  mode = 'light',
  ...boxProps
}) => {
  const themeSize = size !== undefined ? sizeMap[size] : token('sizes.size.450');

  const Icon: IconComponent = GoalIconMap[name][mode];

  return (
    <Box
      as="span"
      className={className}
      css={{ display: 'flex', ...cssProp }}
      style={{ width: themeSize, height: themeSize }}
      {...boxProps}
    >
      <Icon />
    </Box>
  );
};
